/**
 * CONSENT-BASED PAGE READING — the person's choice and the page snapshot (owner request 2026-10-18; ADR 0072, proposed;
 * design §31.25 PAGE-READING row, 2026-10-18).
 *
 * The help assistant asks each signed-in person once whether it may read the whole page they are on. The choice is the
 * person's (`help_page_reading`, migration 0165): `allowed`, `declined` or `unasked` (the default, read as declined).
 *
 * - **Fields only** (`declined`, `unasked`): the page sends only `page_context` — the screen, the step, the fillable
 *   fields with their label, kind, options and whether they are filled (values only where the registry allows them).
 * - **Whole page** (`allowed`): before each message the portal also sends a `page_snapshot`: the visible page as a
 *   size-capped, structured list — headings, text, tables (truncated, marked), status and error messages, and every
 *   control with a stable ref, its label, type, value, options and whether it is enabled. Never a password, secret, API
 *   key, card or second-factor control, a hidden input, a file input or the help panel itself (the portal's builder
 *   leaves them out; `apps/web-app/src/help/snapshot/**`). The help host accepts a snapshot only while the person's
 *   SAVED choice is `allowed` (it reads it from the database, never from the frame) and drops it otherwise.
 * - **wetalk.io** (an anonymous visitor): the public page's own text only — no controls, no personal data there — and
 *   no question asked.
 *
 * A snapshot is model input for ONE turn: fenced by a nonce as untrusted page content, never stored in a help record,
 * never logged. Imports nothing (the protocol guards read it).
 */
/** The person's choice (`help_page_reading.choice`, 0165's CHECK). `unasked` is read as `declined`. */
export const HELP_PAGE_READING = ["allowed", "declined", "unasked"];
export function isHelpPageReading(value) {
    return typeof value === "string" && HELP_PAGE_READING.includes(value);
}
export function helpPageReadingModeOf(choice) {
    return choice === "allowed" ? "whole_page" : "fields_only";
}
// ── the snapshot ─────────────────────────────────────────────────────────────────────────────
/** A `page_snapshot` frame is at most this many bytes (UTF-8) as sent — about 2,500–3,500 model tokens. */
export const HELP_PAGE_SNAPSHOT_BYTE_LIMIT = 10_240;
/** At most this many items in one snapshot. */
export const HELP_SNAPSHOT_ITEM_LIMIT = 160;
/** One heading, text or status line is at most this many characters (longer is cut and ends in {@link HELP_SNAPSHOT_CUT}). */
export const HELP_SNAPSHOT_TEXT_CHAR_LIMIT = 400;
/** One table cell, a control's label, value or option is at most this many characters. */
export const HELP_SNAPSHOT_CELL_CHAR_LIMIT = 120;
/** A table keeps at most this many columns and rows; `more` says how many rows were left out. */
export const HELP_SNAPSHOT_TABLE_COLUMN_LIMIT = 8;
export const HELP_SNAPSHOT_TABLE_ROW_LIMIT = 12;
/** A control keeps at most this many options. */
export const HELP_SNAPSHOT_OPTION_LIMIT = 24;
/** The marker a cut text ends in. */
export const HELP_SNAPSHOT_CUT = "…[truncated]";
/** A control's ref: `<form_id>.<field code>` for an actionable one (a fill target), `c<n>` for any other. */
export const HELP_SNAPSHOT_REF = /^(?:[a-z][a-z0-9_]{0,63}\.[a-z][a-z0-9_]{0,63}|c\d{1,4})$/;
export const HELP_SNAPSHOT_CONTROL_TYPE = [
    "text",
    "number",
    "time",
    "date",
    "select",
    "checkbox",
    "radio",
    "textarea",
    "button",
    "link",
    "switch",
    "other",
];
function fieldsOf(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value) ? value : null;
}
function only(fields, required, optional = []) {
    for (const key of Object.keys(fields))
        if (!required.includes(key) && !optional.includes(key))
            return false;
    return required.every((key) => key in fields && fields[key] !== undefined);
}
function isLine(value, max, min = 1) {
    if (typeof value !== "string")
        return false;
    const count = Array.from(value).length;
    return count >= min && count <= max;
}
function isCells(value) {
    return Array.isArray(value) && value.length <= HELP_SNAPSHOT_TABLE_COLUMN_LIMIT && value.every((one) => isLine(one, HELP_SNAPSHOT_CELL_CHAR_LIMIT, 0));
}
function isSnapshotItem(value) {
    const f = fieldsOf(value);
    if (f === null)
        return false;
    switch (f["kind"]) {
        case "heading":
            return only(f, ["kind", "level", "text"]) && typeof f["level"] === "number" && Number.isInteger(f["level"]) && f["level"] >= 1 && f["level"] <= 6 && isLine(f["text"], HELP_SNAPSHOT_TEXT_CHAR_LIMIT);
        case "text":
            return only(f, ["kind", "text"]) && isLine(f["text"], HELP_SNAPSHOT_TEXT_CHAR_LIMIT);
        case "status":
            return only(f, ["kind", "tone", "text"]) && (f["tone"] === "error" || f["tone"] === "warning" || f["tone"] === "info") && isLine(f["text"], HELP_SNAPSHOT_TEXT_CHAR_LIMIT);
        case "table": {
            if (!only(f, ["kind", "header", "row", "more"]) || !isCells(f["header"]))
                return false;
            const row = f["row"];
            const more = f["more"];
            return Array.isArray(row) && row.length <= HELP_SNAPSHOT_TABLE_ROW_LIMIT && row.every(isCells) && typeof more === "number" && Number.isInteger(more) && more >= 0;
        }
        case "control": {
            if (!only(f, ["kind", "ref", "label", "type", "enabled", "actionable"], ["value", "option"]))
                return false;
            if (typeof f["ref"] !== "string" || !HELP_SNAPSHOT_REF.test(f["ref"]))
                return false;
            if (!isLine(f["label"], HELP_SNAPSHOT_CELL_CHAR_LIMIT, 0))
                return false;
            if (!HELP_SNAPSHOT_CONTROL_TYPE.includes(f["type"]))
                return false;
            if ("value" in f && typeof f["value"] !== "boolean" && !isLine(f["value"], HELP_SNAPSHOT_CELL_CHAR_LIMIT, 0))
                return false;
            const option = f["option"];
            if ("option" in f && !(Array.isArray(option) && option.length <= HELP_SNAPSHOT_OPTION_LIMIT && option.every((one) => isLine(one, HELP_SNAPSHOT_CELL_CHAR_LIMIT, 0))))
                return false;
            // A ref naming a form and a field exactly when the page says the control is a fill target.
            const named = f["ref"].includes(".");
            return typeof f["enabled"] === "boolean" && typeof f["actionable"] === "boolean" && named === f["actionable"];
        }
        default:
            return false;
    }
}
/** Whether a value is exactly a snapshot within the item and length limits (the byte limit is the frame reader's). */
export function isHelpPageSnapshot(value) {
    const f = fieldsOf(value);
    if (f === null || !only(f, ["item", "truncated"]) || typeof f["truncated"] !== "boolean")
        return false;
    const item = f["item"];
    return Array.isArray(item) && item.length <= HELP_SNAPSHOT_ITEM_LIMIT && item.every(isSnapshotItem);
}
/** A text cut to `limit` characters, ending in {@link HELP_SNAPSHOT_CUT} when it was cut. */
export function cutSnapshotText(text, limit) {
    const chars = Array.from(text);
    if (chars.length <= limit)
        return { text, cut: false };
    const marker = Array.from(HELP_SNAPSHOT_CUT).length;
    return { text: `${chars.slice(0, Math.max(0, limit - marker)).join("")}${HELP_SNAPSHOT_CUT}`, cut: true };
}
//# sourceMappingURL=page-snapshot.js.map